import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { clientsAPI } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import {
  Graph, Lightning, CircleNotch, CheckCircle, X, Warning, Globe, Shield, ArrowUp, ArrowDown
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nx_token')}` } });
const SC = v => v >= 70 ? 'var(--success)' : v >= 40 ? 'var(--warning)' : 'var(--error)';
const Ind = ({ v, l }) => <span className={`flex items-center gap-1 text-[10px] ${v ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>{v ? <CheckCircle size={11} weight="fill" /> : <X size={11} weight="bold" />}{l}</span>;

export default function EntityPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [sel, setSel] = useState('');
  const [audit, setAudit] = useState(null);
  const [running, setRunning] = useState(false);
  const [triggering, setTriggering] = useState('');

  useEffect(() => { clientsAPI.list().then(r => { setClients(r.data); if (r.data.length > 0) setSel(r.data[0].id); }); }, []);
  useEffect(() => { if (!sel) return; axios.get(`${API}/entity/audits/${sel}`, auth()).then(r => { if (r.data.length > 0) setAudit(r.data[0]); }); }, [sel]);

  const handleAudit = async () => {
    setRunning(true);
    try { const r = await axios.post(`${API}/entity/audit/${sel}`, {}, auth()); setAudit(r.data); toast.success(`Entity: ${r.data.entity_score}`); }
    catch { toast.error('Failed'); } finally { setRunning(false); }
  };

  const trigger = (agent) => { setTriggering(agent); toast.success(`${agent} agent triggered`); setTimeout(() => setTriggering(''), 1200); };
  const lm = audit?.live_metrics || {};

  const extractionData = lm.extraction ? Object.entries(lm.extraction.types || {}).map(([k, v]) => ({ name: k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), value: v })) : [];

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="entity-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-[var(--text-primary)] tracking-tight">Entity & Knowledge Graph</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">AI engines retrieve Entities — not keywords. Brand identity is the foundation.</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger className="w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{c.display_name || c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={handleAudit} disabled={running} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
              {running ? <CircleNotch size={16} className="animate-spin mr-2" /> : <Graph size={16} className="mr-2" />}Run Audit
            </Button>
          </div>
        </div>

        {audit ? (<>
          {/* ═══ PRIMARY METRICS ═══ */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="nx-metric-card rounded-lg text-center py-4">
              <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Entity Authority</span>
              <p className="text-5xl font-black font-mono mt-1" style={{ color: SC(audit.entity_score) }}>{audit.entity_score}</p>
              <p className="text-xs text-[var(--text-tertiary)] mt-1">/100</p>
            </div>
            <div className="nx-metric-card rounded-lg py-4">
              <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">SameAs References</span>
              <p className="text-3xl font-black text-[var(--text-primary)] font-mono mt-1">{audit.sameas_count}/8</p>
              <p className="text-[8px] text-[var(--text-tertiary)] mt-1">Target: minimum 8</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {Object.entries(audit.sameas_sources || {}).map(([k, v]) => (
                  <span key={k} className={`px-1 py-0.5 text-[7px] rounded font-mono ${v ? 'bg-[var(--success-bg)] text-[var(--success)]' : 'bg-[var(--error-bg)] text-[var(--error)]'}`}>{k}</span>
                ))}
              </div>
            </div>
            <div className="nx-metric-card rounded-lg py-4">
              <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">NAP Consistency</span>
              <p className="text-3xl font-black font-mono mt-1" style={{ color: audit.nap_consistency >= 98 ? 'var(--success)' : audit.nap_consistency >= 90 ? 'var(--warning)' : 'var(--error)' }}>{audit.nap_consistency}%</p>
              <p className="text-[8px] mt-1" style={{ color: audit.nap_consistency >= 98 ? 'var(--success)' : 'var(--warning)' }}>{audit.nap_consistency >= 98 ? 'Clean Signal' : audit.nap_consistency >= 90 ? 'Minor Gaps' : 'Confusion Risk'}</p>
            </div>
            <div className="nx-metric-card rounded-lg py-4">
              <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Entity Presence</span>
              <p className="text-3xl font-black text-[var(--text-primary)] font-mono mt-1">{audit.presence_count}/5</p>
              <div className="space-y-0.5 mt-2">
                {[['wikipedia', 'Wikipedia'], ['wikidata', 'Wikidata'], ['crunchbase', 'Crunchbase'], ['linkedin', 'LinkedIn'], ['knowledge_panel', 'Knowledge Panel']].map(([k, l]) => (
                  <Ind key={k} v={audit.entity_presence?.[k]} l={l} />
                ))}
              </div>
            </div>
          </div>

          {/* ═══ INTELLIGENCE TABLE ═══ */}
          {audit.intelligence?.length > 0 && (
            <div className="nx-metric-card rounded-lg">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3"><Lightning size={16} className="inline mr-2 text-[var(--warning)]" weight="fill" />Proactive Actions</h3>
              <table className="w-full border-collapse">
                <thead><tr className="border-b border-[var(--border)]"><th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[25%]">Signal</th><th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[40%]">Intelligence</th><th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[35%]">Action</th></tr></thead>
                <tbody>
                  {audit.intelligence.map(r => (
                    <tr key={r.id} className="border-b border-[var(--border-light)]">
                      <td className="p-2"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: r.severity === 'high' ? 'var(--error)' : r.severity === 'low' ? 'var(--success)' : 'var(--warning)' }} /><span className="text-xs text-[var(--text-primary)]">{r.indicator}</span></span></td>
                      <td className="p-2 text-xs text-[var(--text-secondary)]">{r.intelligence}</td>
                      <td className="p-2"><p className="text-[10px] text-[var(--text-secondary)] mb-1">{r.action}</p>
                        <Button size="sm" onClick={() => trigger(r.agent)} disabled={triggering === r.agent} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[10px] h-7 px-3"><Lightning size={10} className="mr-1" />Execute</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ═══ 4 LIVE METRICS ═══ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* M1: Identity Drift */}
            <div className="nx-metric-card rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold text-[var(--text-primary)]"><Shield size={14} className="inline mr-1 text-[var(--accent)]" weight="duotone" />Identity Drift Monitoring</span>
                <span className={`text-[9px] font-mono ${lm.identity_drift?.total > 0 ? 'text-[var(--error)]' : 'text-[var(--success)]'}`}>{lm.identity_drift?.total || 0} alerts</span>
              </div>
              {(lm.identity_drift?.alerts || []).length === 0 ? (
                <p className="text-xs text-[var(--success)] flex items-center gap-1 py-4"><CheckCircle size={14} weight="fill" />No drift detected</p>
              ) : lm.identity_drift.alerts.map((a, i) => (
                <div key={i} className="p-2 rounded border border-[var(--error-border)] bg-[var(--error-bg)] mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-[var(--error)] font-bold">{a.type}</span>
                    <span className="text-[8px] text-[var(--text-tertiary)] font-mono">Confidence: {a.confidence}% | Cited: {a.year_cited}</span>
                  </div>
                  <p className="text-[9px] text-[var(--error)]">AI says: "{a.hallucinated}"</p>
                  <p className="text-[9px] text-[var(--success)]">Correct: "{a.correct}"</p>
                  <p className="text-[8px] text-[var(--text-tertiary)]">Affected: {a.affected_llms?.join(', ')}</p>
                </div>
              ))}
              {lm.identity_drift?.total > 0 && (
                <Button size="sm" onClick={() => trigger('correction')} className="w-full bg-[var(--error)] hover:opacity-90 text-white text-[10px] h-7 mt-1"><Shield size={10} className="mr-1" />Trigger Correction Campaign</Button>
              )}
            </div>

            {/* M2: Entity Sentiment */}
            {lm.sentiment && (
              <div className="nx-metric-card rounded-lg">
                <span className="text-xs font-bold text-[var(--text-primary)] mb-3 block">Entity Sentiment Polarity</span>
                <div className="flex items-end gap-3 mb-3">
                  <p className="text-3xl font-black font-mono" style={{ color: SC(lm.sentiment.overall) }}>{lm.sentiment.overall}/100</p>
                  <div className="flex gap-2 text-[9px] font-mono mb-1">
                    <span className={lm.sentiment.trend_30d >= 0 ? 'text-[var(--success)]' : 'text-[var(--error)]'}>{lm.sentiment.trend_30d > 0 ? '+' : ''}{lm.sentiment.trend_30d}% 30d</span>
                    <span className={lm.sentiment.trend_90d >= 0 ? 'text-[var(--success)]' : 'text-[var(--error)]'}>{lm.sentiment.trend_90d > 0 ? '+' : ''}{lm.sentiment.trend_90d}% 90d</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {[['expertise', 'Expertise'], ['trustworthiness', 'Trustworthiness'], ['modernity', 'Modernity'], ['premium', 'Premium Perception']].map(([k, l]) => (
                    <div key={k} className="flex items-center gap-2">
                      <span className="text-[10px] text-[var(--text-secondary)] w-28">{l}</span>
                      <div className="flex-1 h-2.5 bg-[var(--neutral-bg)] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${lm.sentiment[k]}%`, background: SC(lm.sentiment[k]) }} />
                      </div>
                      <span className="text-[10px] font-mono text-[var(--text-primary)] w-8 text-right">{lm.sentiment[k]}</span>
                      {lm.sentiment[k] < 50 && <Warning size={10} className="text-[var(--error)]" weight="fill" />}
                    </div>
                  ))}
                </div>
                {lm.sentiment.corrective_needed?.length > 0 && (
                  <p className="text-[8px] text-[var(--error)] font-mono mt-2">Corrective needed: {lm.sentiment.corrective_needed.join(', ')}</p>
                )}
              </div>
            )}

            {/* M3: Bilingual Entity Moat */}
            {lm.bilingual_moat && (
              <div className="nx-metric-card rounded-lg">
                <span className="text-xs font-bold text-[var(--text-primary)] mb-3 block"><Globe size={14} className="inline mr-1 text-[var(--accent)]" weight="duotone" />Bilingual Entity Moat</span>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase mb-2">Arabic Entity</p>
                    <Ind v={lm.bilingual_moat.arabic?.wikipedia?.exists} l={`Wikipedia (${lm.bilingual_moat.arabic?.wikipedia?.word_count || 0} words)`} />
                    <Ind v={lm.bilingual_moat.arabic?.wikidata?.name_ar} l="Wikidata Name (AR)" />
                    <Ind v={lm.bilingual_moat.arabic?.wikidata?.description_ar} l="Wikidata Desc (AR)" />
                    <Ind v={lm.bilingual_moat.arabic?.sameas?.al_masdar} l="Al Masdar" />
                    <Ind v={lm.bilingual_moat.arabic?.sameas?.arab_news} l="Arab News" />
                    <p className="text-[8px] text-[var(--text-tertiary)] font-mono mt-1">GCC dirs: {lm.bilingual_moat.arabic?.sameas?.gcc_directories || 0}</p>
                    <p className="text-[8px] text-[var(--accent)] font-mono">AI citations (AR): {lm.bilingual_moat.arabic?.wikipedia?.citations_in_arabic_ai || 0}</p>
                    {!lm.bilingual_moat.arabic?.wikipedia?.exists && (
                      <Button size="sm" onClick={() => trigger('entity_ar')} className="mt-2 w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[8px] h-6">Draft Arabic Wikipedia Entry</Button>
                    )}
                  </div>
                  <div>
                    <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase mb-2">Hindi Entity</p>
                    <Ind v={lm.bilingual_moat.hindi?.wikipedia?.exists} l={`Wikipedia (${lm.bilingual_moat.hindi?.wikipedia?.word_count || 0} words)`} />
                    <p className="text-[8px] text-[var(--text-tertiary)] font-mono mt-1">Devanagari refs: {lm.bilingual_moat.hindi?.sameas?.devanagari_refs || 0}</p>
                    <p className="text-[8px] text-[var(--accent)] font-mono">Vernacular citations: {lm.bilingual_moat.hindi?.sameas?.vernacular_citations || 0}</p>
                  </div>
                </div>
              </div>
            )}

            {/* M4: Extraction Efficiency */}
            {lm.extraction && (
              <div className="nx-metric-card rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-[var(--text-primary)]">Entity Extraction Efficiency</span>
                  <span className="text-[9px] text-[var(--text-tertiary)] font-mono">Benchmark: {lm.extraction.benchmark}%</span>
                </div>
                <p className="text-2xl font-black font-mono mb-3" style={{ color: lm.extraction.average >= 35 ? 'var(--success)' : 'var(--warning)' }}>{lm.extraction.average}%</p>
                <div className="space-y-2">
                  {extractionData.map((d, i) => (
                    <div key={d.name} className="flex items-center gap-2">
                      <span className="text-[10px] text-[var(--text-secondary)] w-28">{d.name}</span>
                      <div className="flex-1 h-2.5 bg-[var(--neutral-bg)] rounded-full overflow-hidden relative">
                        <div className="absolute h-full border-r border-dashed border-[var(--warning-border)]" style={{ left: `${lm.extraction.benchmark}%` }} />
                        <div className="h-full rounded-full" style={{ width: `${d.value}%`, background: d.value >= 35 ? 'var(--success)' : d.value >= 20 ? 'var(--warning)' : 'var(--error)' }} />
                      </div>
                      <span className="text-[10px] font-mono text-[var(--text-primary)] w-10 text-right">{d.value}%</span>
                    </div>
                  ))}
                </div>
                <p className="text-[8px] text-[var(--warning)] font-mono mt-2 italic">Lowest: "{lm.extraction.lowest}" — agent will restructure into definition-first + table format</p>
              </div>
            )}
          </div>
        </>) : (
          <div className="nx-metric-card rounded-lg text-center py-16">
            <Graph size={48} className="mx-auto mb-4 text-[var(--text-tertiary)]" />
            <p className="text-[var(--text-secondary)]">Click "Run Audit" to analyze brand entity architecture</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
